import moment from "moment-timezone";
import { getCampaignTimezoneSync } from "../../helpers/getCampaignTimezone";
import { slotCapacity, slotKindOf, sellableProfiles, productFamilyIds, fulfillmentOf, isMailbox } from "./catalog";

export const LATAM_CURRENCIES = [
  { code: "ARS", name: "Peso argentino", symbol: "$" },
  { code: "BOB", name: "Boliviano", symbol: "Bs" },
  { code: "BRL", name: "Real brasileño", symbol: "R$" },
  { code: "CLP", name: "Peso chileno", symbol: "$" },
  { code: "COP", name: "Peso colombiano", symbol: "$" },
  { code: "CRC", name: "Colón costarricense", symbol: "₡" },
  { code: "CUP", name: "Peso cubano", symbol: "$" },
  { code: "DOP", name: "Peso dominicano", symbol: "RD$" },
  { code: "GTQ", name: "Quetzal", symbol: "Q" },
  { code: "HNL", name: "Lempira", symbol: "L" },
  { code: "MXN", name: "Peso mexicano", symbol: "$" },
  { code: "NIO", name: "Córdoba", symbol: "C$" },
  { code: "PAB", name: "Balboa", symbol: "B/." },
  { code: "PEN", name: "Sol peruano", symbol: "S/" },
  { code: "PYG", name: "Guaraní", symbol: "₲" },
  { code: "UYU", name: "Peso uruguayo", symbol: "$U" },
  { code: "VES", name: "Bolívar", symbol: "Bs." },
  { code: "USD", name: "Dólar estadounidense", symbol: "US$" },
  { code: "EUR", name: "Euro", symbol: "€" },
];

export const CURRENCY_SYMBOLS = [...new Set(LATAM_CURRENCIES.map((c) => c.symbol))];

export const defaultSymbol = (code) =>
  LATAM_CURRENCIES.find((c) => c.code === String(code || "").toUpperCase())?.symbol || "$";

let negocioFormat = {
  currency: "COP",
  symbol: "$",
  currencyFx: "USD",
  exchangeRate: 1,
  timezone: getCampaignTimezoneSync() || "America/Bogota",
  pointsSystemEnabled: true,
  pointsEquivalence: 1000,
  profitPercent: 50,
};

export const setNegocioFormat = (patch) => {
  negocioFormat = { ...negocioFormat, ...patch };
  if (!negocioFormat.timezone) negocioFormat.timezone = "America/Bogota";
  if (!negocioFormat.symbol) negocioFormat.symbol = defaultSymbol(negocioFormat.currency);
};

export const getNegocioFormat = () => negocioFormat;

export const pointsSystemOn = () => {
  const v = getNegocioFormat().pointsSystemEnabled;
  return v !== false && v !== 0 && v !== "0";
};

export const clientPointsOn = (customer) =>
  pointsSystemOn() && !!customer && customer.pointsEnabled !== false && customer.pointsEnabled !== 0;

/** Revendedor sin cashback: el saldo y su icono no se muestran en ninguna ficha de ese cliente. */
export const customerWalletVisible = (customer) => {
  if (!customer || customer.customerType !== "reseller") return true;
  return customer.pointsEnabled !== false && customer.pointsEnabled !== 0 && customer.pointsEnabled !== "0";
};

export const pointsEquivalenceOf = () => {
  const n = Number(getNegocioFormat().pointsEquivalence || 1000);
  return Number.isFinite(n) && n > 0 ? n : 1000;
};

/** Otorgar: valorVenta / equivalencia configurada. */
export const pointsEarnedFromSale = (price) => {
  const eq = pointsEquivalenceOf();
  const value = Number(price || 0);
  if (!(value > 0) || !(eq > 0)) return 0;
  return value / eq;
};

/**
 * Canje 1:1 (sin equivalencia): puntos necesarios = precio en las mismas unidades.
 * @deprecated Usar pointsNeededToRedeem; se mantiene por compatibilidad de imports.
 */
export const pointsForPrice = (price) => pointsNeededToRedeem(price);

/** Canje: 1 punto = 1 unidad de precio. No divide por equivalencia. */
export const pointsNeededToRedeem = (price) => {
  const value = Number(price || 0);
  return value > 0 ? value : 0;
};

/** Precio a canjear: el monto de la moneda elegida (local o divisa), 1 punto = 1 unidad. */
export const redeemPriceOf = ({ currency, price, priceFx } = {}) => {
  const amount = currency === "fx" ? Number(priceFx || 0) : Number(price || 0);
  return amount > 0 ? amount : 0;
};

/** Valor de un servicio en la moneda del monedero del cliente (misma regla que el backend: toWalletAmount). */
export const walletNeedOf = ({ customer, currency, price, priceFx } = {}) => {
  const fmt = getNegocioFormat();
  const code = String(customer?.walletCurrency || fmt.currencyFx || "USD").toUpperCase();
  const isLocal = code === String(fmt.currencyLocal || fmt.currency || "COP").toUpperCase();
  const local = Number(price || 0);
  if (isLocal) return WALLET_ZERO_DECIMALS.includes(code) ? Math.round(local) : local;
  if (currency === "fx" && Number(priceFx || 0) > 0) return Number(priceFx);
  return Math.round((local / exchangeRateOf()) * 100) / 100;
};

/** Solo se ofrece pagar con saldo cuando el saldo cubre el valor completo. */
export const canPayWithWallet = ({ customer, need } = {}) =>
  Number(need || 0) > 0 && Number(customer?.walletBalance || 0) + 0.009 >= Number(need);

const WALLET_ZERO_DECIMALS = ["COP", "CLP", "PYG", "CRC"];

/** Saldo del monedero en la moneda del cliente (local para su país, divisa para el resto). */
export const formatWallet = (n, currency) => {
  const code = String(currency || getNegocioFormat().currencyFx || "USD").toUpperCase();
  const value = Number(n || 0);
  const config = getNegocioFormat();
  const symbol = code === String(config.currency || "COP").toUpperCase() ? config.symbol || defaultSymbol(code) : defaultSymbol(code);
  return WALLET_ZERO_DECIMALS.includes(code)
    ? `${symbol}${Math.round(value).toLocaleString("es-CO")} ${code}`
    : `${symbol}${(Math.round(value * 100) / 100).toFixed(2)} ${code}`;
};

export const rafflePrizeLive = (customer) => {
  const prize = customer?.rafflePrize;
  if (!prize || prize.status !== "pending") return null;
  const left = daysLeft(prize.expiresAt);
  if (left == null || left < 0) return null;
  if (customer.kind !== "active" && customer.kind !== "expiring") return null;
  if (!(Number(customer.activeSubs || 0) > 0)) return null;
  return prize;
};

export const canPayWithPrize = (customer) => !!rafflePrizeLive(customer);

export const money = (n, currency, symbol) => {
  const value = Number(n || 0);
  const fmt = getNegocioFormat();
  const mark = symbol || (!currency || currency === fmt.currency ? fmt.symbol : defaultSymbol(currency)) || defaultSymbol(currency || fmt.currency);
  const amount = Math.abs(value).toLocaleString("es-CO", {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 2,
  });
  return value < 0 ? `-${mark} ${amount}` : `${mark} ${amount}`;
};

export const moneyCompact = (n, currency, symbol) => {
  const value = Number(n || 0);
  const abs = Math.abs(value);
  const fmt = getNegocioFormat();
  const mark = symbol || (!currency || currency === fmt.currency ? fmt.symbol : defaultSymbol(currency)) || defaultSymbol(currency || fmt.currency);
  if (abs >= 1_000_000) {
    const compact = (value / 1_000_000).toLocaleString("es-CO", { maximumFractionDigits: abs >= 10_000_000 ? 1 : 2 });
    return `${mark} ${compact} M`;
  }
  if (abs >= 10_000) {
    const compact = (value / 1_000).toLocaleString("es-CO", { maximumFractionDigits: abs >= 100_000 ? 0 : 1 });
    return `${mark} ${compact} mil`;
  }
  return money(n, currency, symbol);
};

export const moneyFx = (n) => {
  const fmt = getNegocioFormat();
  const code = fmt.currencyFx || "USD";
  return money(n, code, code === fmt.currency ? fmt.symbol : defaultSymbol(code));
};

export const exchangeRateOf = () => {
  const rate = Number(getNegocioFormat().exchangeRate || 1);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
};

export const toLocalPrice = (value, currency) => {
  const n = Number(value || 0);
  if (String(currency) !== "fx") return n;
  return n * exchangeRateOf();
};

export const toFxPrice = (local) => Number(local || 0) / exchangeRateOf();

export const moneyPair = (local, fx) => {
  const localN = Number(local || 0);
  const fxN = fx != null && fx !== "" && Number.isFinite(Number(fx)) ? Number(fx) : toFxPrice(localN);
  return { local: localN, fx: fxN };
};

export const moneyBoth = (local, fx) => {
  const pair = moneyPair(local, fx);
  return `${money(pair.local)} · ${moneyFx(pair.fx)}`;
};

export const splitFromTyped = (value, currency) => {
  const n = Number(value || 0);
  if (String(currency) === "fx") return { local: toLocalPrice(n, "fx"), fx: n };
  return { local: n, fx: toFxPrice(n) };
};

export const profitOf = (localPrice, cost) => Number(localPrice || 0) - Number(cost || 0);

export const profitPercentOf = () => {
  const n = Number(getNegocioFormat().profitPercent);
  return Number.isFinite(n) && n >= 0 ? n : 50;
};

export const roundMoneyAmount = (n) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return 0;
  return Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
};

export const suggestedSalePrice = (account, saleDays = 30) =>
  roundMoneyAmount(periodSlotCost(account, saleDays) * (1 + profitPercentOf() / 100));

export const periodSlotPrice = (account, saleDays = 30) => {
  const stored = Number(account?.salePrice || 0);
  const days = Math.max(1, Number(saleDays || 30));
  if (stored > 0) return (stored * days) / 30;
  return suggestedSalePrice(account, days);
};

/** Par local/divisa del periodo: respeta la divisa guardada a mano en la cuenta. */
export const periodSlotPricePair = (account, saleDays = 30) => {
  const local = periodSlotPrice(account, saleDays);
  const storedFx = Number(account?.salePriceFx || 0);
  const days = Math.max(1, Number(saleDays || 30));
  const fx = storedFx > 0 && Number(account?.salePrice || 0) > 0 ? (storedFx * days) / 30 : toFxPrice(local);
  return { local, fx };
};

/** Precio a cobrar/renovar de un servicio: suscripción → cuenta. */
export const renewPriceOf = (sub) => {
  const fromSub = Number(sub?.price);
  if (Number.isFinite(fromSub) && fromSub > 0) return fromSub;
  const fromAccount = Number(sub?.account?.salePrice);
  if (Number.isFinite(fromAccount) && fromAccount > 0) return fromAccount;
  return 0;
};

export const withSuggestedSalePrice = (form, patch = {}) => {
  const next = { ...form, ...patch };
  if (Number(next.salePrice) > 0) return patch;
  const fromProduct = Number(next.suggestedPrice || 0);
  if (fromProduct > 0) {
    const currency = next.suggestedCurrency === "fx" ? "fx" : "local";
    return {
      ...patch,
      salePrice: fromProduct,
      salePriceFx: Number(next.suggestedPriceFx) > 0 ? Number(next.suggestedPriceFx) : toFxPrice(fromProduct),
      saleCurrency: currency,
      currency,
    };
  }
  const sale = suggestedSalePrice(next, 30);
  if (!(sale > 0)) return patch;
  const currency = next.saleCurrency === "fx" ? "fx" : "local";
  return {
    ...patch,
    salePrice: sale,
    salePriceFx: toFxPrice(sale),
    saleCurrency: currency,
    currency,
  };
};

export const accountIsFullSale = (account) => {
  if (!account) return false;
  if (account.saleType === "full" || slotKindOf(account) === "none") return true;
  return accountOccupancy(account).saleType === "full";
};

const CART_PULSE_MS = 5 * 60 * 1000;
const cartPulse = new Map();
const subPulse = new Map();

export const markCartPulse = (contactId, kind) => {
  const id = Number(contactId);
  if (!id || !kind) return;
  const prev = cartPulse.get(id) || {};
  cartPulse.set(id, { ...prev, [kind]: Date.now() + CART_PULSE_MS });
};

export const cartPulseOn = (contactId, kind, now = Date.now()) => {
  const until = cartPulse.get(Number(contactId))?.[kind];
  return Number(until) > now;
};

export const markSubPulse = (subscriptionId, kind) => {
  const id = Number(subscriptionId);
  if (!id || !kind) return;
  const prev = subPulse.get(id) || {};
  subPulse.set(id, { ...prev, [kind]: Date.now() + CART_PULSE_MS });
};

export const subPulseOn = (subscriptionId, kind, now = Date.now()) => {
  const until = subPulse.get(Number(subscriptionId))?.[kind];
  return Number(until) > now;
};

/** Marca el icono "Enviar datos actualizados" ~5 min para esos servicios/carritos. */
export const pulseAccessUpdate = ({ contactId, subscriptionId, subscriptionIds } = {}) => {
  if (contactId) markCartPulse(contactId, "update");
  const ids = [
    ...(subscriptionId ? [subscriptionId] : []),
    ...((subscriptionIds || []).filter(Boolean)),
  ];
  ids.forEach((id) => markSubPulse(id, "update"));
};

/** Campos del puesto que el cliente recibe al entrar. */
export const profileAccessChanged = (profile, next) =>
  String(next?.name || "") !== String(profile?.name || "") ||
  String(next?.pin || "") !== String(profile?.pin || "") ||
  String(next?.memberEmail || "") !== String(profile?.memberEmail || "") ||
  String(next?.siteUrl || "") !== String(profile?.siteUrl || "") ||
  String(next?.siteUser || "") !== String(profile?.siteUser || "") ||
  (next?.sitePassword != null &&
    String(next.sitePassword || "") !== String(profile?.sitePassword || ""));

/** Credenciales de cuenta que disparan reenvío al cliente. */
export const accountAccessChanged = (account, next) =>
  String(next?.email || "") !== String(account?.email || "") ||
  (!!next?.password && String(next.password) !== String(account?.password || "")) ||
  String(next?.url || "") !== String(account?.url || "") ||
  String(next?.code || "") !== String(account?.code || "") ||
  String(next?.ip || "") !== String(account?.ip || "");

export const formatPoints = (n) => {
  const value = Number(n || 0);
  return value.toLocaleString("es-CO", {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 2,
  });
};

export const companyTz = () => getNegocioFormat().timezone || getCampaignTimezoneSync() || "America/Bogota";

export const formatDateTime = (value) => {
  if (!value) return "—";
  const tz = companyTz();
  const raw = String(value);
  const m = raw.includes("T") || raw.includes("Z") || /[+-]\d{2}:?\d{2}$/.test(raw)
    ? moment.tz(raw, tz)
    : moment.tz(raw.replace("T", " ").slice(0, 19), "YYYY-MM-DD HH:mm:ss", tz);
  if (!m.isValid()) return raw.slice(0, 16);
  return m.format("DD/MM/YYYY HH:mm");
};

export const formatDate = (value) => {
  if (!value) return "—";
  const tz = companyTz();
  const raw = String(value);
  const m = raw.includes("T") || raw.includes("Z") || /[+-]\d{2}:?\d{2}$/.test(raw)
    ? moment.tz(raw, tz)
    : moment.tz(raw.slice(0, 10), "YYYY-MM-DD", tz);
  if (!m.isValid()) return raw.slice(0, 10);
  return m.format("DD/MM/YYYY");
};

export const inDatePeriod = (value, period, extras = {}) => {
  if (!period || period === "all") return true;
  if (!value) return false;
  const tz = companyTz();
  const d = moment.tz(value, tz);
  if (!d.isValid()) return false;
  const now = moment.tz(tz);
  if (period === "today") return d.format("YYYY-MM-DD") === now.format("YYYY-MM-DD");
  if (period === "week") return d.isSameOrAfter(now.clone().startOf("isoWeek")) && d.isSameOrBefore(now.clone().endOf("isoWeek"));
  if (period === "month") {
    const year = extras.year != null && extras.year !== "" ? Number(extras.year) : now.year();
    const month = extras.month != null && extras.month !== "" ? Number(extras.month) : now.month();
    return d.month() === month && d.year() === year;
  }
  if (period === "year") {
    const year = extras.year != null && extras.year !== "" ? Number(extras.year) : now.year();
    return d.year() === year;
  }
  return true;
};

export const randomPin = (length = 4) => {
  const n = Math.max(1, Math.min(12, Number(length) || 4));
  let out = "";
  for (let i = 0; i < n; i += 1) out += String(Math.floor(Math.random() * 10));
  return out;
};

export const pinLengthOf = (account, currentPin = "") => {
  const counts = {};
  (account?.profiles || []).forEach((p) => {
    const len = String(p?.pin || "").trim().length;
    if (len) counts[len] = (counts[len] || 0) + 1;
  });
  const common = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  return Number(common) || String(currentPin || "").trim().length || Number(account?.pinLength) || 4;
};

export const PHONE_PREFIXES = [
  { prefix: "1809", countryCode: "DO", countryName: "República Dominicana" },
  { prefix: "1829", countryCode: "DO", countryName: "República Dominicana" },
  { prefix: "1849", countryCode: "DO", countryName: "República Dominicana" },
  { prefix: "1787", countryCode: "PR", countryName: "Puerto Rico" },
  { prefix: "1939", countryCode: "PR", countryName: "Puerto Rico" },
  { prefix: "593", countryCode: "EC", countryName: "Ecuador" },
  { prefix: "591", countryCode: "BO", countryName: "Bolivia" },
  { prefix: "595", countryCode: "PY", countryName: "Paraguay" },
  { prefix: "598", countryCode: "UY", countryName: "Uruguay" },
  { prefix: "507", countryCode: "PA", countryName: "Panamá" },
  { prefix: "506", countryCode: "CR", countryName: "Costa Rica" },
  { prefix: "503", countryCode: "SV", countryName: "El Salvador" },
  { prefix: "504", countryCode: "HN", countryName: "Honduras" },
  { prefix: "502", countryCode: "GT", countryName: "Guatemala" },
  { prefix: "505", countryCode: "NI", countryName: "Nicaragua" },
  { prefix: "351", countryCode: "PT", countryName: "Portugal" },
  { prefix: "57", countryCode: "CO", countryName: "Colombia" },
  { prefix: "58", countryCode: "VE", countryName: "Venezuela" },
  { prefix: "52", countryCode: "MX", countryName: "México" },
  { prefix: "51", countryCode: "PE", countryName: "Perú" },
  { prefix: "56", countryCode: "CL", countryName: "Chile" },
  { prefix: "54", countryCode: "AR", countryName: "Argentina" },
  { prefix: "55", countryCode: "BR", countryName: "Brasil" },
  { prefix: "34", countryCode: "ES", countryName: "España" },
  { prefix: "39", countryCode: "IT", countryName: "Italia" },
  { prefix: "33", countryCode: "FR", countryName: "Francia" },
  { prefix: "49", countryCode: "DE", countryName: "Alemania" },
  { prefix: "44", countryCode: "GB", countryName: "Reino Unido" },
  { prefix: "1", countryCode: "US", countryName: "Estados Unidos / Canadá" },
].sort((a, b) => b.prefix.length - a.prefix.length);

export const DEFAULT_PHONE_PREFIX = "57";

export const phoneDigits = (value) => String(value || "").replace(/\D/g, "");

export const phonesMatch = (a, b) => {
  const left = phoneDigits(a);
  const right = phoneDigits(b);
  if (left.length < 8 || right.length < 8) return false;
  if (left === right) return true;
  const shorter = left.length < right.length ? left : right;
  const longer = left.length < right.length ? right : left;
  return shorter.length >= 10 && longer.endsWith(shorter);
};

export const findProfilePicByPhone = (phone, contacts) => {
  const list = Array.isArray(contacts) ? contacts : [];
  const hit = list.find((row) => {
    const pic = String(row?.profilePicUrl || "");
    if (!pic || pic.includes("nopicture")) return false;
    return phonesMatch(phone, row?.number);
  });
  return hit?.profilePicUrl || null;
};

export const countryFromPhone = (value) => {
  const digits = phoneDigits(value);
  if (!digits) return { countryCode: null, countryName: null, prefix: null };
  const found = PHONE_PREFIXES.find((row) => digits.startsWith(row.prefix));
  if (!found) return { countryCode: null, countryName: null, prefix: null };
  return { countryCode: found.countryCode, countryName: found.countryName, prefix: found.prefix };
};

export const flagEmoji = (iso) => {
  const code = String(iso || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "🏳️";
  return String.fromCodePoint(...[...code].map((ch) => 127397 + ch.charCodeAt(0)));
};

export const splitPhone = (value, fallbackPrefix = DEFAULT_PHONE_PREFIX) => {
  const raw = String(value || "");
  if (/^tg:/i.test(raw)) {
    const fallback = PHONE_PREFIXES.find((row) => row.prefix === fallbackPrefix) || PHONE_PREFIXES.find((row) => row.countryCode === "CO");
    return { prefix: fallback.prefix, local: "", countryCode: fallback.countryCode, countryName: fallback.countryName };
  }
  const digits = phoneDigits(raw);
  const found = PHONE_PREFIXES.find((row) => digits.startsWith(row.prefix) && digits.length > row.prefix.length);
  if (found) {
    return {
      prefix: found.prefix,
      local: digits.slice(found.prefix.length),
      countryCode: found.countryCode,
      countryName: found.countryName,
    };
  }
  const fallback = PHONE_PREFIXES.find((row) => row.prefix === fallbackPrefix) || PHONE_PREFIXES.find((row) => row.countryCode === "CO");
  return {
    prefix: fallback.prefix,
    local: digits,
    countryCode: fallback.countryCode,
    countryName: fallback.countryName,
  };
};

export const joinPhone = (prefix, local) => `${phoneDigits(prefix)}${phoneDigits(local)}`;

export const formatPhoneDisplay = (value) => {
  const digits = phoneDigits(value);
  if (!digits) return "";
  const parts = splitPhone(digits);
  return parts.local ? `+${parts.prefix} ${parts.local}` : `+${digits}`;
};

export const parseCustomerCsv = (text) => {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return [];
  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else quoted = !quoted;
      } else if ((ch === "," || ch === ";") && !quoted) {
        out.push(cur.trim());
        cur = "";
      } else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const header = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/^\uFEFF/, "").trim());
  const idx = (names) => header.findIndex((h) => names.includes(h));
  const map = {
    name: idx(["nombre", "name", "cliente"]),
    prefix: idx(["prefijo", "prefix", "lada"]),
    phone: idx(["telefono", "teléfono", "phone", "numero", "número"]),
    type: idx(["tipo", "type"]),
    email: idx(["email", "correo", "mail"]),
  };
  const hasHeader = Object.values(map).some((i) => i >= 0);
  const body = hasHeader ? lines.slice(1) : lines;
  return body.slice(0, 1000).map((line, index) => {
    const cols = splitLine(line);
    const pick = (key, fallback) => {
      const i = map[key];
      if (hasHeader && i >= 0) return cols[i] || "";
      return cols[fallback] || "";
    };
    return {
      line: hasHeader ? index + 2 : index + 1,
      name: pick("name", 0),
      prefix: pick("prefix", 1),
      phone: pick("phone", 2),
      type: pick("type", 3),
      email: pick("email", 4),
    };
  });
};

export const parseAccountCsv = (text) => {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return [];
  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else quoted = !quoted;
      } else if ((ch === "," || ch === ";") && !quoted) {
        out.push(cur.trim());
        cur = "";
      } else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const header = splitLine(lines[0]).map((h) => h.toLowerCase().replace(/^\uFEFF/, "").trim());
  const idx = (names) => header.findIndex((h) => names.includes(h));
  const map = {
    product: idx(["producto", "product", "servicio", "plataforma"]),
    email: idx(["correo", "email", "cuenta", "usuario"]),
    password: idx(["contraseña", "contrasena", "password", "clave", "pass"]),
    cost: idx(["costo", "cost"]),
    salePrice: idx(["precio", "precio_venta", "saleprice", "precio venta"]),
    startDate: idx(["inicio", "fecha inicio", "startdate", "fecha_inicio", "contratacion"]),
    durationDays: idx(["dias", "días", "durationdays", "duracion", "duración"]),
    saleType: idx(["tipo", "saletype", "venta"]),
    slotCount: idx(["slots", "perfiles", "puestos", "slotcount"]),
    supplier: idx(["proveedor", "supplier"]),
    url: idx(["url", "link"]),
    ip: idx(["ip"]),
    originCountry: idx(["pais", "país", "country", "origen"]),
    notes: idx(["notas", "notes", "comentario"]),
    pinEnabled: idx(["pin", "pinenabled"]),
  };
  const hasHeader = Object.values(map).some((i) => i >= 0);
  const body = hasHeader ? lines.slice(1) : lines;
  return body.slice(0, 500).map((line, index) => {
    const cols = splitLine(line);
    const pick = (key, fallback) => {
      const i = map[key];
      if (hasHeader && i >= 0) return cols[i] || "";
      return fallback != null ? cols[fallback] || "" : "";
    };
    return {
      line: hasHeader ? index + 2 : index + 1,
      product: pick("product", 0),
      email: pick("email", 1),
      password: pick("password", 2),
      cost: pick("cost", 3),
      salePrice: pick("salePrice", 4),
      startDate: pick("startDate", 5),
      durationDays: pick("durationDays", 6),
      saleType: pick("saleType", 7),
      slotCount: pick("slotCount", null),
      supplier: pick("supplier", null),
      url: pick("url", null),
      ip: pick("ip", null),
      originCountry: pick("originCountry", null),
      notes: pick("notes", null),
      pinEnabled: pick("pinEnabled", null),
    };
  });
};

export const customerCode = (id, customerType) =>
  `${customerType === "reseller" ? "REV" : "CLI"}-${String(id || 0).padStart(3, "0")}`;

export const monthLabel = (monthIndex, locale = "es") => {
  const labels = {
    es: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    pt: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
  };
  return (labels[locale] || labels.es)[Number(monthIndex) || 0] || "";
};

export const percentLabel = (value) => {
  const n = Number(value || 0);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

export const statusColor = (s) => {
  const map = {
    active: "#2e7d32",
    paid: "#2e7d32",
    available: "#2e7d32",
    pending: "#ed6c02",
    pending_payment: "#ed6c02",
    expiring: "#ed6c02",
    soon: "#ed6c02",
    today: "#c62828",
    assigned: "#c62828",
    expired: "#9e9e9e",
    cancelled: "#c62828",
    fault: "#c62828",
    disabled: "#9e9e9e",
    idle: "#607d8b",
    occupied: "#c62828",
    sold: "#6a1b9a",
    sale: "#2e7d32",
    full: "#1565c0",
    none: "#607d8b",
    final: "#1565c0",
    reseller: "#6a1b9a",
    pointsOn: "#2e7d32",
    pointsOff: "#9e9e9e",
  };
  return map[s] || "#607d8b";
};

export const matchesQuery = (row, query, fields) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  return fields.some((field) => String(field || "").toLowerCase().includes(q));
};

export const publicFileUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const name = String(value).replace(/^\/public\//, "").replace(/^\//, "");
  return `${String(backend).replace(/\/$/, "")}/public/${name}`;
};

export const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "PR";

export const addDaysIso = (iso, days) => {
  if (!iso) return "";
  return moment(String(iso).slice(0, 10), "YYYY-MM-DD")
    .add(Number(days || 0), "days")
    .format("YYYY-MM-DD");
};

export const todayIso = () => moment.tz(companyTz()).format("YYYY-MM-DD");

export const daysLeft = (endDate) => {
  if (!endDate) return null;
  const tz = companyTz();
  const end = moment.tz(String(endDate).slice(0, 10), "YYYY-MM-DD", tz).startOf("day");
  const today = moment.tz(tz).startOf("day");
  return end.diff(today, "days");
};

export const canRenewService = (sub) => {
  const left = daysLeft(sub?.endDate);
  return sub?.status !== "cancelled" && Number.isFinite(left) && left <= 3;
};

export const dueKind = (sub) => {
  if (!sub || sub.status === "cancelled") return null;
  const left = daysLeft(sub.endDate);
  if (left == null) return null;
  if (left < 0) return "expired";
  if (left === 0) return "today";
  if (left <= 3) return "soon";
  return "ok";
};

export const isRecentIso = (value, now = Date.now()) => {
  if (!value) return false;
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return false;
  const delta = now - t;
  return delta >= -60 * 1000 && delta <= CART_PULSE_MS;
};

export const cartCanDelete = (items) => {
  const list = items || [];
  if (!list.length) return true;
  return list.every((s) => {
    if (s.status === "cancelled" || s.status === "expired") return true;
    const left = daysLeft(s.endDate);
    return left != null && left < 0;
  });
};

export const cartHasRecentAdd = (items, now = Date.now(), contactId) =>
  cartPulseOn(contactId, "add", now) ||
  (items || []).some((s) => isRecentIso(s.createdAt, now) || isRecentIso(s.sale?.createdAt, now));

// Solo datos de acceso cambiados (marca notifyPending del backend); cualquier otra edición no ofrece reenviar.
export const cartHasRecentUpdate = (items, now = Date.now(), contactId) =>
  cartPulseOn(contactId, "update", now) || (items || []).some((s) => subHasRecentUpdate(s, now));

/** Edición de un servicio que cambia lo que recibe el cliente: plataforma, cuenta o puesto (perfil/PIN). */
export const serviceAccessChanged = (sub, payload) =>
  Number(payload?.productId || 0) !== Number(sub?.productId || 0) ||
  Number(payload?.accountId || 0) !== Number(sub?.accountId || 0) ||
  Number(payload?.profileId || 0) !== Number(sub?.profileId || 0);

/** Puesto del servicio para mostrar junto al correo: perfil y PIN, o correo invitado / usuario del sitio. */
export const subSlotText = (sub) => {
  const profile = sub?.profile;
  const parts = [];
  if (profile?.name) parts.push(profile.name);
  if (profile?.pin) parts.push(`PIN ${profile.pin}`);
  if (!parts.length && (sub?.memberEmail || profile?.memberEmail)) parts.push(sub.memberEmail || profile.memberEmail);
  if (!parts.length && profile?.siteUser) parts.push(profile.siteUser);
  return parts.join(" · ");
};

export const isExpiredService = (sub) => {
  if (!sub || sub.status === "cancelled") return false;
  if (sub.status === "expired") return true;
  const left = daysLeft(sub.endDate);
  return left != null && left < 0;
};

export const cartHasRecentRenew = (items, now = Date.now(), contactId) =>
  cartPulseOn(contactId, "renewed", now) ||
  (!(items || []).some(isExpiredService) &&
    (items || []).some((s) => {
      if (s.notifyPending === true || s.notifyPending === 1) return false;
      if (!isRecentIso(s.updatedAt, now)) return false;
      const created = new Date(s.createdAt).getTime();
      const updated = new Date(s.updatedAt).getTime();
      return Number.isFinite(created) && updated - created > 3000;
    }));

/** Servicio con datos recién cambiados: el icono de envío dura 5 minutos. */
export const subHasRecentUpdate = (sub, now = Date.now()) => {
  if (!sub?.id) return false;
  if (subPulseOn(sub.id, "update", now)) return true;
  const pending = sub.notifyPending === true || sub.notifyPending === 1;
  if (!pending || !isRecentIso(sub.updatedAt, now)) return false;
  const created = new Date(sub.createdAt).getTime();
  const updated = new Date(sub.updatedAt).getTime();
  return Number.isFinite(created) && updated - created > 3000;
};

/** Servicio recién renovado: muestra icono de envío ~5 min. */
export const subHasRecentRenew = (sub, contactId, now = Date.now()) => {
  if (!sub?.id) return false;
  if (subPulseOn(sub.id, "renewed", now)) return true;
  if (cartPulseOn(contactId, "renewed", now) && isRecentIso(sub.updatedAt, now)) return true;
  if (isExpiredService(sub)) return false;
  // Cambio de cuenta/perfil pendiente de avisar: es actualización, no renovación.
  if (subPulseOn(sub.id, "update", now) || sub.notifyPending === true || sub.notifyPending === 1) return false;
  if (!isRecentIso(sub.updatedAt, now)) return false;
  const created = new Date(sub.createdAt).getTime();
  const updated = new Date(sub.updatedAt).getTime();
  return Number.isFinite(created) && updated - created > 3000;
};

export const liveKind = (sub, salesById) => {
  if (!sub || sub.status === "cancelled") return null;
  const due = dueKind(sub);
  if (due === "expired" || sub.status === "expired") return "expired";
  const sale = sub.sale || salesById?.get?.(Number(sub.saleId));
  if (sub.status === "pending" || sale?.status === "pending_payment") return "pending";
  if (due === "soon" || due === "today") return due;
  return "active";
};

export const PROFILE_PLACEHOLDER =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="32" fill="#e2e8f0"/>
      <circle cx="32" cy="24" r="12" fill="#94a3b8"/>
      <path d="M14 58c1.5-13 10-20 18-20s16.5 7 18 20" fill="#94a3b8"/>
    </svg>`
  );

export const accountIsNonRenewable = (account) => [false, 0, "0", "false"].includes(account?.isRenewable);

export const accountIsAutopay = (account) => account?.billingType === "autopay";

const accountSkipsExpiry = (account) =>
  accountIsAutopay(account) || isMailbox(account) || fulfillmentOf(account) === "panel";

const accountIsDateExpired = (account, left) =>
  !accountSkipsExpiry(account) && left != null && left < 0;

export const accountOccupancy = (account) => {
  if (fulfillmentOf(account) === "panel") {
    const cap = Math.max(0, Number(account.slotCount || 0));
    const left = daysLeft(account.endDate);
    const expired = accountIsDateExpired(account, left) || account.status === "expired";
    const blocked = expired || account.status === "fault" || account.status === "idle";
    const available = !blocked && account.status === "available" && cap > 0;
    return { saleType: "profile", free: available ? cap : 0, busy: 0, left, available, occupied: !blocked && cap <= 0, cap, expired, blocked };
  }
  const profiles = account.profiles || [];
  const saleType = account.saleType === "full" || (!profiles.length && account.slotCount === 0) ? "full" : "profile";
  const cap = slotCapacity(account) || profiles.length;
  const busy = profiles.filter((p) => p.status === "assigned").length;
  const left = daysLeft(account.endDate);
  const expired = accountIsDateExpired(account, left) || account.status === "expired";
  const blocked = expired || account.status === "fault" || account.status === "idle";
  const activeOrders = Number(
    account.orders != null
      ? account.orders
      : Array.isArray(account.subscriptions)
        ? account.subscriptions.filter((s) => s && s.status !== "cancelled").length
        : 0
  );
  const fullSold =
    saleType === "full" &&
    (account.status === "occupied" ||
      account.status === "sold" ||
      activeOrders > 0);
  const rawFree =
    saleType === "full"
      ? account.status === "available" && !fullSold
        ? 1
        : 0
      : sellableProfiles(account).length;
  const free = blocked ? 0 : rawFree;
  const available = !blocked && free > 0;
  const occupied =
    saleType === "full"
      ? fullSold || (!available && !blocked)
      : (cap || profiles.length) > 0 && free === 0 && !blocked;
  return { saleType, free, busy, left, available, occupied, cap, expired, blocked };
};

export const accountStock = (account) => {
  const occ = accountOccupancy(account || {});
  if (occ.blocked || occ.expired) return 0;
  if (occ.saleType === "full") return occ.available ? 1 : 0;
  return occ.free;
};

export const accountUsesPin = (account) => {
  if (!account || account.saleType === "full") return false;
  if (slotKindOf(account) !== "pin_profile") return false;
  if (account.pinEnabled === false || account.pinEnabled === 0) return false;
  if (account.pinEnabled === true || account.pinEnabled === 1) return true;
  return (account.profiles || []).some((p) => String(p.pin || "").trim());
};

export const accountLiveStatus = (account) => {
  if (!account) return "available";
  if (account.status === "fault") return "fault";
  if (account.status === "idle") return "idle";
  const occ = accountOccupancy(account);
  if (occ.expired || account.status === "expired") return "expired";
  if (!accountSkipsExpiry(account) && occ.left != null && occ.left <= 3 && !occ.occupied) return "expiring";
  if (occ.occupied) return "sold";
  return "available";
};

export const visibleValue = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "—" || text === "-") return "";
  return text;
};

export const accountSellable = (account, preferredType) => {
  if (!account) return false;
  if (String(account.status) !== "available") return false;
  const occ = accountOccupancy(account);
  const skipDays = accountIsAutopay(account) || isMailbox(account) || fulfillmentOf(account) === "panel";
  if (!skipDays && account.endDate && (occ.left == null || occ.left <= 3)) return false;
  if (!occ.available) return false;
  if (preferredType === "full") return occ.saleType === "full";
  if (preferredType === "profile") return occ.saleType === "profile";
  return true;
};

/** Disponible (sin falla/inactiva) y no vencida: se pueden tomar puestos de ella. */
export const accountLive = (account) => {
  if (!account || String(account.status) !== "available") return false;
  const skipDays = accountIsAutopay(account) || isMailbox(account) || fulfillmentOf(account) === "panel";
  if (skipDays || !account.endDate) return true;
  const left = accountOccupancy(account).left;
  return left != null && left >= 0;
};

export const accountPrizeEligible = (account, preferredType) => {
  if (!account) return false;
  if (String(account.status) !== "available") return false;
  const occ = accountOccupancy(account);
  const skipDays = accountIsAutopay(account) || isMailbox(account) || fulfillmentOf(account) === "panel";
  if (!skipDays && (occ.left == null || occ.left < 30)) return false;
  if (!occ.available) return false;
  if (preferredType === "full") return occ.saleType === "full";
  if (preferredType === "profile") return occ.saleType === "profile";
  return true;
};

export const purchaseDaysOf = (account) => {
  const days = Number(account?.durationDays || 0);
  if (days > 0) return days;
  return accountIsAutopay(account) ? 365 : 30;
};

export const periodSlotCost = (account, saleDays = 30) => {
  const purchase = Number(account?.cost || 0);
  if (!(purchase > 0)) return 0;
  if (isMailbox(account)) return purchase;
  const bought = purchaseDaysOf(account);
  const panel = fulfillmentOf(account) === "panel";
  const slots = panel
    ? Math.max(1, Number(account?.product?.maxProfiles || account?.slotCount || 1))
    : account?.saleType === "full" || slotKindOf(account) === "none"
      ? 1
      : Math.max(1, slotCapacity(account) || Number(account?.slotCount || 1));
  const days = Math.max(1, Number(saleDays || 30));
  return (purchase * days) / bought / slots;
};

export const suggestAccountAndProfile = (accounts, { product, products, preferredType } = {}) => {
  const family = new Set(productFamilyIds(products, product));
  const pool = (accounts || []).filter((a) => {
    if (family.size) {
      if (!family.has(Number(a.productId))) return false;
    } else if (product && Number(a.productId) !== Number(product.id)) {
      return false;
    }
    return accountSellable(a, preferredType);
  });
  pool.sort((a, b) => {
    const oa = accountOccupancy(a);
    const ob = accountOccupancy(b);
    const freeDiff = Number(ob.free || 0) - Number(oa.free || 0);
    if (freeDiff) return freeDiff;
    const leftA = oa.left == null ? -999 : Number(oa.left);
    const leftB = ob.left == null ? -999 : Number(ob.left);
    return leftB - leftA;
  });
  const account = pool[0] || null;
  if (!account) return { accountId: "", profileId: "", account: null };
  if (preferredType === "full" || account.saleType === "full") {
    return { accountId: account.id, profileId: "", account };
  }
  const free = sellableProfiles(account);
  const profile = free.length ? free[Math.floor(Math.random() * free.length)] : null;
  return { accountId: account.id, profileId: profile?.id || "", account };
};

// Contactos del bot de Telegram: «tg:u<id>» o «tg:usuario». No son teléfonos aunque tengan dígitos.
export const isTelegramPlaceholder = (value) => /^tg:/i.test(String(value || "").trim());

export const looksLikePhone = (value) => !isTelegramPlaceholder(value) && phoneDigits(value).length >= 8;

export const canReachCustomer = (customer) =>
  !!(looksLikePhone(customer?.number) || isTelegramPlaceholder(customer?.number) || customer?.telegram || customer?.whatsappReachable);

export const storedPhone = (value) => (looksLikePhone(value) ? phoneDigits(value) : "");

export const sampleServiceBlock = () =>
  `🎬 *Amazon Prime* | *30* días
💰 Precio: $ 6.000
📧 *Correo:* \`\`\`demo@correo.com\`\`\`
🔑 *Contraseña:* \`\`\`Clave123\`\`\`
👤 *Perfil/usuario:* Perfil 1 - 1847
📅 *Vence:* 18/10/2026`;

export const DEFAULT_REMINDER_MESSAGE = `\`\`\`👋 Hola {{nombre_cliente}} 👋, ¿Cómo estás? 🙂
Solo pasamos por aquí para recordarle que sus servicios están por suspenderse:\`\`\`

{{servicios}}

👉 Evite la suspensión y continúe disfrutando su contenido favorito.
> Si ya realizó el pago, envíenos el comprobante para validar la activación.`;

export const NEGOCIO_MESSAGE_VARS = [
  { key: "nombre_servicio", label: "Servicio", sample: "Amazon Prime", color: "#0288d1" },
  { key: "plan", label: "Plan", sample: "Standard", color: "#00695c" },
  { key: "correo", label: "Email", sample: "demo@correo.com", color: "#7b1fa2" },
  { key: "usuario", label: "Usuario", sample: "cliente01", color: "#6a1b9a" },
  { key: "contrasena", label: "Contraseña", sample: "Clave123", color: "#c62828" },
  { key: "perfil", label: "Perfil / puesto", sample: "Sitio 1", color: "#2e7d32" },
  { key: "pin", label: "PIN", sample: "1847", color: "#ef6c00" },
  { key: "correo_miembro", label: "Correo miembro", sample: "cliente@gmail.com", color: "#4527a0" },
  { key: "codigo_entrega", label: "Código / link", sample: "XXXX-YYYY", color: "#ad1457" },
  { key: "url_panel", label: "URL panel", sample: "http://servidor:8080", color: "#00838f" },
  { key: "url_sitio", label: "URL sitio", sample: "https://cliente.midominio.com", color: "#00695c" },
  { key: "usuario_sitio", label: "Usuario sitio", sample: "admin", color: "#4527a0" },
  { key: "contrasena_sitio", label: "Clave sitio", sample: "ClaveSitio1", color: "#c62828" },
  { key: "id_externo", label: "ID recarga", sample: "123456789", color: "#37474f" },
  { key: "dispositivos", label: "Dispositivos", sample: "3", color: "#455a64" },
  { key: "pais_cuenta", label: "País cuenta", sample: "Colombia", color: "#1565c0" },
  { key: "garantia_dias", label: "Garantía (días)", sample: "30", color: "#6a1b9a" },
  { key: "lote", label: "Lote", sample: "10", color: "#546e7a" },
  { key: "fecha_vencimiento", label: "Vencimiento", sample: "18/10/2026", color: "#1565c0" },
  { key: "nombre_cliente", label: "Cliente", sample: "Laura Barrios", color: "#00695c" },
  { key: "precio", label: "Precio", sample: "$16.000", color: "#4527a0" },
  { key: "dias", label: "Días", sample: "3", color: "#546e7a" },
  { key: "servicios", label: "Servicios", sample: sampleServiceBlock(), color: "#ad1457" },
  { key: "usdt", label: "USDT exacto", sample: "2.00", color: "#f0b90b" },
  { key: "pay_id", label: "Pay ID", sample: "294603789", color: "#f0b90b" },
  { key: "vence_pago", label: "Vence pago", sample: "25/09/2026 18:00", color: "#ed6c02" },
];

export const fillNegocioMessage = (text, extras = {}) => {
  let out = String(text || "");
  NEGOCIO_MESSAGE_VARS.forEach((item) => {
    const value = extras[item.key] != null ? extras[item.key] : item.sample;
    out = out.replace(new RegExp(`\\{\\{\\s*${item.key}\\s*\\}\\}`, "gi"), value);
    out = out.replace(new RegExp(`\\{\\s*${item.key}\\s*\\}`, "gi"), value);
  });
  out = out.replace(/\{\{\s*servicio\s*\}\}/gi, extras.nombre_servicio || "Amazon Prime");
  out = out.replace(/\{\{\s*email\s*\}\}/gi, extras.correo || "demo@correo.com");
  out = out.replace(/\{\{\s*vencimiento\s*\}\}/gi, extras.fecha_vencimiento || "18/10/2026");
  return out;
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const formatWhatsappInline = (value) =>
  escapeHtml(value)
    .replace(/\*(.+?)\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/~(.+?)~/g, "<s>$1</s>");

export const whatsappPreviewHtml = (text) => {
  const raw = String(text || "");
  const parts = [];
  const re = /```([\s\S]*?)```/g;
  let last = 0;
  let match;
  while ((match = re.exec(raw))) {
    if (match.index > last) parts.push({ type: "text", value: raw.slice(last, match.index) });
    parts.push({ type: "code", value: match[1] });
    last = match.index + match[0].length;
  }
  if (last < raw.length) parts.push({ type: "text", value: raw.slice(last) });
  return parts
    .map((part) => {
      if (part.type === "code") return `<span class="wa-mono">${escapeHtml(part.value)}</span>`;
      return part.value
        .split("\n")
        .map((line) => {
          const quote = line.match(/^\s*>\s?(.*)$/);
          if (quote) return `<span class="wa-quote">${formatWhatsappInline(quote[1])}</span>`;
          return formatWhatsappInline(line);
        })
        .join("<br/>");
    })
    .join("");
};

export const accountPath = (id) => (id ? `/negocio/cuentas?accountId=${id}` : "/negocio/cuentas");
export const customerPath = (contactId) =>
  contactId ? `/negocio/clientes?contactId=${contactId}` : "/negocio/clientes";
export const incomePath = ({ contactId, saleId } = {}) => {
  const q = new URLSearchParams();
  if (contactId) q.set("contactId", String(contactId));
  if (saleId) q.set("saleId", String(saleId));
  const qs = q.toString();
  return qs ? `/negocio/ingresos?${qs}` : "/negocio/ingresos";
};
export const servicesPath = (contactId, add) => {
  const q = new URLSearchParams();
  if (contactId) q.set("contactId", String(contactId));
  if (add) q.set("add", "1");
  const qs = q.toString();
  return qs ? `/negocio/servicios?${qs}` : "/negocio/servicios";
};
export const cobrosPath = (contactId) =>
  contactId ? `/negocio/cobros?contactId=${contactId}` : "/negocio/cobros";
export const salePath = (id) => incomePath({ saleId: id });

export const readQuery = (search) => {
  try {
    return Object.fromEntries(new URLSearchParams(search || (typeof window !== "undefined" ? window.location.search : "")));
  } catch {
    return {};
  }
};

export const replaceQuery = (history, location, patch) => {
  const params = new URLSearchParams(location?.search || "");
  Object.keys(patch || {}).forEach((key) => {
    const value = patch[key];
    if (value == null || value === "") params.delete(key);
    else params.set(key, String(value));
  });
  const search = params.toString();
  const next = search ? `?${search}` : "";
  if ((location?.search || "") === next) return;
  history.replace({ pathname: location.pathname, search: next });
};

export const colorFromKey = (key) => {
  const palette = ["#0284c7", "#0d9488", "#7c3aed", "#db2777", "#ea580c", "#0369a1", "#059669", "#4338ca"];
  const s = String(key || "?");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const fxCacheKey = (from, to) => `watoolx.negocio.fx.${from}.${to}`;

const readFxCache = (from, to) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(fxCacheKey(from, to)) || "null");
    if (parsed && parsed.date === todayIso() && Number(parsed.rate) > 0) return parsed;
  } catch {
    /* ignore */
  }
  return null;
};

const writeFxCache = (from, to, payload) => {
  try {
    localStorage.setItem(
      fxCacheKey(from, to),
      JSON.stringify({ ...payload, date: todayIso() })
    );
  } catch {
    /* ignore quota */
  }
};

const pickRate = (rate, source, updatedAt) => {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) throw new Error("missing");
  return { rate: n, source, updatedAt: updatedAt || todayIso() };
};

export async function fetchExchangeRate(fx, local, { force = false } = {}) {
  const from = String(fx || "USD").trim().toUpperCase();
  const to = String(local || "COP").trim().toUpperCase();
  if (!from || !to) throw new Error("currency");
  if (from === to) {
    return { rate: 1, source: "same", updatedAt: todayIso(), cached: false };
  }
  if (!force) {
    const cached = readFxCache(from, to);
    if (cached) return { ...cached, cached: true };
  }
  const fromL = from.toLowerCase();
  const toL = to.toLowerCase();
  const sources = [
    async () => {
      const res = await fetch(
        `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${fromL}.min.json`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error("fx");
      const data = await res.json();
      return pickRate(data?.[fromL]?.[toL], "currency-api", data.date);
    },
    async () => {
      const res = await fetch(
        `https://latest.currency-api.pages.dev/v1/currencies/${fromL}.min.json`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error("fx");
      const data = await res.json();
      return pickRate(data?.[fromL]?.[toL], "currency-api", data.date);
    },
    async () => {
      const res = await fetch(`https://open.er-api.com/v6/latest/${encodeURIComponent(from)}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("fx");
      const data = await res.json();
      return pickRate(data?.rates?.[to], "open.er-api.com", data.time_last_update_utc);
    },
  ];
  let lastError = null;
  for (const run of sources) {
    try {
      const result = await run();
      writeFxCache(from, to, result);
      return { ...result, cached: false };
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError || new Error("fx");
}


